import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { HeaderTooltip } from '../HeaderTooltip';
import { currency } from '../formatters';
import { genericHybridPresentation } from './genericHybridPresentation';
import type { StrategyPresentation } from './types';

type SecureCareLikeDeltas = {
  epLumpSumPaid?: number;
  epLumpSumDays?: number;
  epLumpSumDailyRate?: number;
  indemnitySurplusApplied?: number;
};

function EpLumpTooltipContent({
  days,
  dailyRate,
  total,
}: {
  days: number;
  dailyRate: number;
  total: number;
}) {
  return (
    <TooltipContent className="text-xs">
      <div className="mb-1 font-semibold">Waiting period reimbursement</div>
      <div className="mb-1 text-[10px] text-gray-300">
        SecureCare IV pays the elimination period back once it is satisfied. Days served are
        reimbursed at the daily benefit in force on the satisfaction date, drawn from the
        Acceleration pool first and then Extension.
      </div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">Days served</span>
        <span className="font-semibold tabular-nums">{Math.round(days)}</span>
        <span className="text-gray-300">Daily rate</span>
        <span className="font-semibold tabular-nums">{currency(dailyRate)}/day</span>
        <span className="text-gray-300">Lump sum</span>
        <span className="font-semibold tabular-nums">
          {Math.round(days)} × {currency(dailyRate)} = {currency(total)}
        </span>
      </div>
    </TooltipContent>
  );
}

// Securian SecureCare III / IV. The table SHAPE is identical to the generic
// two-pool hybrid (Acceleration/base pool drawn first, then Extension/COB;
// cash-indemnity payout with a surplus reserve; static life face), so this
// reuses genericHybridPresentation and overrides only what SecureCare does
// differently: the death benefit depletes PROPORTIONALLY to the remaining
// Acceleration pool (secureCareStrategy) rather than dollar-for-dollar, and
// SecureCare IV pays a retroactive waiting-period lump that no other
// generic-hybrid product has. All values are read off precomputed month
// state/deltas — no strategy math is re-implemented here.
export const secureCarePresentation: StrategyPresentation = {
  ...genericHybridPresentation,

  // Without this the lump row shows a headline payout (PaidCell adds
  // epLumpSumPaid) with nothing in the breakdown explaining where it came from.
  paidCellExtras: month => {
    const sd = month.deltas.strategyDeltas as SecureCareLikeDeltas | undefined;
    const surplusApplied = sd?.indemnitySurplusApplied ?? 0;
    const epLump = sd?.epLumpSumPaid ?? 0;
    const covered = month.deltas.payout.ltcCoveragePaid;
    const withSurplus = month.deltas.payout.ltcPayoutToCost;

    if (surplusApplied === 0 && epLump === 0) return null;

    return (
      <>
        {surplusApplied > 0 && withSurplus !== covered && (
          <div className="inline-block text-[10px] text-emerald-700">
            +{currency(surplusApplied)} surplus
          </div>
        )}
        {epLump > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-block cursor-help border-b border-dotted border-amber-300 text-[10px] text-amber-700">
                +{currency(epLump)} WP reimbursement
              </div>
            </TooltipTrigger>
            <EpLumpTooltipContent
              days={sd?.epLumpSumDays ?? 0}
              dailyRate={sd?.epLumpSumDailyRate ?? 0}
              total={epLump}
            />
          </Tooltip>
        )}
      </>
    );
  },

  copy: {
    ...genericHybridPresentation.copy,
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. Held at the at-issue face — it does NOT inflate. LTC draws from the Acceleration pool reduce it in proportion to the remaining Acceleration pool (NOT dollar-for-dollar); Extension draws do not. Whatever remains is paid to beneficiaries."
        formulas={[
          {
            label: 'Death benefit',
            expression: 'open death benefit × (Acceleration pool after draw ÷ Acceleration pool before draw)',
          },
          { label: 'Floor', expression: 'max(above, Residual Death Benefit)' },
        ]}
        notes="Static face (the inflation rider grows the LTC pools, not the life face). Proportional depletion means an inflated acceleration draw never over-reduces the flat face; the death benefit reaches the residual as the face is (near-)fully accelerated. On SecureCare IV the retroactive waiting-period lump draws from Acceleration too, so it reduces the face on the row it is paid."
      />
    ),
  },
};
